// users.jsx - ユーザー管理（TanStack Query版）
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import './usermanagement.css';
import useNotificationStore from '../stores/notificationStore'; // 通知用

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

// ソートオプション
const SORT_OPTIONS = [
  { value: 'user_id', label: '登録順' },
  { value: 'name', label: '名前' },
  { value: 'email', label: 'メール' },
  { value: 'last_login', label: '最終ログイン' },
];

const SORT_ORDER_OPTIONS = [
  { value: 'asc', label: '昇順' },
  { value: 'desc', label: '降順' },
];

// 役割の定義
const ROLE_CONFIG = {
  '管理者': { className: 'um-badge--admin', editable: false },
  '運用者': { className: 'um-badge--operator', editable: true },
  '利用者': { className: 'um-badge--user', editable: true },
};

// 編集可能な役割（管理者以外）
const EDITABLE_ROLES = ['利用者', '運用者'];

/* ========================================
 * ヘルパー関数
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const getRoleBadgeClass = (role) => {
  return ROLE_CONFIG[role]?.className || 'um-badge--user';
};

/* ========================================
 * モーダルコンポーネント：ユーザー編集
 * ======================================== */
function UserFormModal({ isOpen, onClose, mutation, user, isMe }) {
  const [formData, setFormData] = useState({
    user_name: '',
    phone: '',
    org_role: '利用者',
    is_active: true,
  });

  const [errors, setErrors] = useState({});

  const isAdmin = user?.org_role === '管理者';
  const canChangeRoleAndStatus = !isMe && !isAdmin;

  // ユーザーが変わったらフォームを初期化
  React.useEffect(() => {
    if (user) {
      setFormData({
        user_name: user.user_name || '',
        phone: user.phone || '',
        org_role: user.org_role || '利用者',
        is_active: user.is_active ?? true,
      });
    }
    setErrors({});
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.user_name.trim()) newErrors.user_name = '名前は必須です';
    if (!formData.phone.trim()) newErrors.phone = '電話番号は必須です';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      user_name: formData.user_name,
      phone: formData.phone,
      
    };

    if (canChangeRoleAndStatus) {
      submitData.org_role = formData.org_role;
      submitData.is_active = formData.is_active;
    }

    mutation.mutate({ userId: user.user_id, data: submitData });
  };

  return (
    <div className="umModalOverlay" onClick={onClose}>
      <div className="umModalContent" onClick={(e) => e.stopPropagation()}>
        <div className="umModalHeader">
          <h2 className="umModalTitle">
            ユーザーを編集
            {isMe && <span className="umModalTitleBadge umModalTitleBadge--me">自分</span>}
            {isAdmin && <span className="umModalTitleBadge umModalTitleBadge--admin">管理者</span>}
          </h2>
          <button type="button" className="umModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="umModalBody">
          {(isMe || isAdmin) && (
            <div className="umEditNotice">
              {isMe && isAdmin && (
                <p>🔒 自分自身（管理者）のため、役割と権限は変更できません。</p>
              )}
              {isMe && !isAdmin && (
                <p>🔒 自分自身のため、役割と権限は変更できません。</p>
              )}
              {!isMe && isAdmin && (
                <p>🔒 管理者のため、役割と権限は変更できません。</p>
              )}
            </div>
          )}

          <div className="umFormGrid">
            <div className="umFormGroup umFormGroup-full">
              <label htmlFor="user_name" className="umFormLabel">
                名前 <span className="umRequired">*</span>
              </label>
              <input
                id="user_name"
                name="user_name"
                className={`umFormInput ${errors.user_name ? 'is-error' : ''}`}
                value={formData.user_name}
                onChange={handleChange}
                placeholder="例）山田 太郎"
              />
              {errors.user_name && <span className="umErrorText">{errors.user_name}</span>}
            </div>

            <div className="umFormGroup umFormGroup-full">
              <label className="umFormLabel">メールアドレス</label>
              <input
                className="umFormInput umFormInput-readonly"
                value={user.user_email || ''}
                readOnly
                disabled
              />
              <span className="umFormHint">※メールアドレスは変更できません</span>
            </div>

            <div className="umFormGroup">
              <label htmlFor="phone" className="umFormLabel">
                電話番号 <span className="umRequired">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                className={`umFormInput ${errors.phone ? 'is-error' : ''}`}
                value={formData.phone}
                onChange={handleChange}
                placeholder="例）090-1234-5678"
              />
              {errors.phone && <span className="umErrorText">{errors.phone}</span>}
            </div>

         
            <div className="umFormGroup">
              <label htmlFor="org_role" className="umFormLabel">役割</label>
              {canChangeRoleAndStatus ? (
                <div className="umFormSelectWrapper">
                  <select
                    id="org_role"
                    name="org_role"
                    className="umFormSelect"
                    value={formData.org_role}
                    onChange={handleChange}
                  >
                    {EDITABLE_ROLES.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="umFormReadonlyValue">
                  <span className={`um-badge ${getRoleBadgeClass(formData.org_role)}`}>
                    {formData.org_role}
                  </span>
                </div>
              )}
            </div>

            <div className="umFormGroup">
              <label className="umFormLabel">権限(無効にするとログインできなくなります)</label>
              {canChangeRoleAndStatus ? (
                <label className="umCheckboxLabel">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span className="umCheckboxText">有効</span>
                </label>
              ) : (
                <div className="umFormReadonlyValue">
                  <span className={`um-badge ${formData.is_active ? 'um-badge--active' : 'um-badge--inactive'}`}>
                    {formData.is_active ? '有効' : '無効'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="umModalFooter">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? '更新中...' : '更新する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ========================================
 * モーダルコンポーネント：ユーザー詳細
 * ======================================== */
function UserDetailModal({ isOpen, onClose, user, onEdit, isMe }) {
  if (!isOpen || !user) return null;

  const isAdmin = user.org_role === '管理者';

  return (
    <div className="umModalOverlay" onClick={onClose}>
      <div className="umModalContent" onClick={(e) => e.stopPropagation()}>
        <div className="umModalHeader">
          <h2 className="umModalTitle">ユーザー詳細</h2>
          <button type="button" className="umModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className="umModalBody">
          <div className="userHeader">
            <div className="uh-row">
              <div className="uh-nameArea">
                <h2 className="uh-title">{user.user_name}</h2>
                {isMe && <span className="um-me-tag">自分</span>}
              </div>
              <div className="uh-badges">
                <span className={`um-badge ${getRoleBadgeClass(user.org_role)}`}>
                  {user.org_role || '未設定'}
                </span>
                <span className={`um-badge ${user.is_active ? 'um-badge--active' : 'um-badge--inactive'}`}>
                  {user.is_active ? '有効' : '無効'}
                </span>
              </div>
            </div>
          </div>

          <h3 className="sectionH3">基本情報</h3>
          <div className="infoGrid">
            <div className="infoRow">
              <span className="infoLabel">メールアドレス</span>
              <span className="infoValue">
                {user.user_email ? (
                  <a href={`mailto:${user.user_email}`} className="um-link">
                    {user.user_email}
                  </a>
                ) : '未設定'}
              </span>
            </div>
            <div className="infoRow">
              <span className="infoLabel">電話番号</span>
              <span className="infoValue">
                {user.phone ? (
                  <a href={`tel:${user.phone}`} className="um-link">
                    {user.phone}
                  </a>
                ) : '未設定'}
              </span>
            </div>
          </div>

          <h3 className="sectionH3">システム情報</h3>
          <div className="infoGrid">
            <div className="infoRow">
              <span className="infoLabel">最終ログイン</span>
              <span className="infoValue">{formatDateTime(user.last_login)}</span>
            </div>
            <div className="infoRow">
              <span className="infoLabel">登録日時</span>
              <span className="infoValue">{formatDateTime(user.created_at)}</span>
            </div>
          </div>

          {(isMe || isAdmin) && (
            <div className="umDetailNotice">
              {isMe && isAdmin && (
                <p>🔒 自分自身（管理者）のため、役割と権限の変更はできません。名前・電話番号・稼働時間は編集可能です。</p>
              )}
              {isMe && !isAdmin && (
                <p>🔒 自分自身のため、役割と権限の変更はできません。名前・電話番号・稼働時間は編集可能です。</p>
              )}
              {!isMe && isAdmin && (
                <p>🔒 管理者のため、役割と権限の変更はできません。名前・電話番号・稼働時間は編集可能です。</p>
              )}
            </div>
          )}
        </div>

        <div className="umModalFooter">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onEdit(user)}
          >
            ✏️ 編集
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================
 * モーダルコンポーネント：確認
 * ======================================== */
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, targetName, warning, confirmText, confirmButtonClass = 'btn-danger' }) {
  if (!isOpen) return null;

  return (
    <div className="umModalOverlay" onClick={onClose}>
      <div className="umModalContent umModalSmall" onClick={(e) => e.stopPropagation()}>
        <div className="umModalHeader">
          <h2 className="umModalTitle">{title}</h2>
          <button type="button" className="umModalClose" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className="umModalBody">
          <p className="umConfirmMessage">{message}</p>
          {targetName && (
            <p className="umConfirmTarget"><strong>{targetName}</strong></p>
          )}
          {warning && <p className="umConfirmWarning">{warning}</p>}
        </div>

        <div className="umModalFooter">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            キャンセル
          </button>
          <button type="button" className={`btn ${confirmButtonClass}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================
 * メインコンポーネント：ユーザー管理
 * ======================================== */
export default function UserManagement() {
  const queryClient = useQueryClient();

  // フィルター用
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // ソート用
  const [sortField, setSortField] = useState('user_id');
  const [sortOrder, setSortOrder] = useState('asc');

  // モーダル制御
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // 招待URL関連
  const [copied, setCopied] = useState(false);

  // 確認モーダル用
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    targetName: '',
    warning: '',
    confirmText: '',
    confirmButtonClass: 'btn-danger',
    onConfirm: null,
  });

  // 自分のメールアドレス
  const meEmail = '';

  // 通知メッセージ
  const { notification, showNotification } = useNotificationStore();

  /* ===== データ取得（useQuery） ===== */
  const { data: usersData, isLoading, isError, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/users_list/`);
      if (!res.data?.success) {
        throw new Error('ユーザー一覧の取得に失敗しました');
      }
      return res.data.items || [];
    },
  });

  const { data: inviteData } = useQuery({
    queryKey: ['inviteUrl'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/invitations/check/`);
      if (res.data?.success && res.data.url) {
        return res.data.url;
      }
      return '';
    },
  });

  const users = usersData || [];
  const inviteUrl = inviteData || '';

  /* ===== ユーザー更新（useMutation） ===== */
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, data }) => {
      const res = await axios.patch(`${API_URL}/api/admin/users_update/${userId}/`, data);
      if (!res.data?.success) {
        throw new Error(res.data?.message || '更新に失敗しました');
      }
      return res.data;
    },
    onSuccess: () => {
      showNotification('ユーザー情報を更新しました');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsFormOpen(false);
      setEditingUser(null);
    },
    onError: (err) => {
      showNotification(err.message || '保存に失敗しました', 'error');
    },
  });

  /* ===== 招待URL作成（useMutation） ===== */
  const createInviteMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(`${API_URL}/api/admin/invitations/`);
      const url = res?.data?.url || '';
      if (!url) throw new Error('URLが取得できませんでした');
      return url;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inviteUrl'] });
      setCopied(false);
    },
    onError: () => {
      showNotification('招待URLの作成に失敗しました', 'error');
    },
  });

  /* ===== 招待URL削除（useMutation） ===== */
  const deleteInviteMutation = useMutation({
    mutationFn: async (token) => {
      await axios.delete(`${API_URL}/api/admin/invitations/delete/`, {
        data: { token },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inviteUrl'] });
      setCopied(false);
      closeConfirmModal();
    },
    onError: () => {
      showNotification('招待URLの削除に失敗しました', 'error');
      closeConfirmModal();
    },
  });

  /* ===== フィルタリングとソート ===== */
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    let result = users.filter((user) => {
      const matchesQuery =
        !q ||
        (user.user_name || '').toLowerCase().includes(q) ||
        (user.user_email || '').toLowerCase().includes(q) ||
        (user.phone || '').toLowerCase().includes(q);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);

      const matchesRole = !roleFilter || user.org_role === roleFilter;

      return matchesQuery && matchesStatus && matchesRole;
    });

    result = [...result].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'user_id':
          aVal = parseInt(a.user_id, 10) || 0;
          bVal = parseInt(b.user_id, 10) || 0;
          break;
        case 'name':
          aVal = (a.user_name || '').toLowerCase();
          bVal = (b.user_name || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.user_email || '').toLowerCase();
          bVal = (b.user_email || '').toLowerCase();
          break;
        case 'last_login':
          aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
          bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [users, searchQuery, statusFilter, roleFilter, sortField, sortOrder]);

  /* ===== モーダル関連 ===== */
  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      targetName: '',
      warning: '',
      confirmText: '',
      confirmButtonClass: 'btn-danger',
      onConfirm: null,
    });
  };

  /* ===== イベントハンドラ ===== */
  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setRoleFilter('');
    setSortField('user_id');
    setSortOrder('asc');
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsDetailOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };

  // 招待URL作成
  const createInvite = () => {
    if (inviteUrl) return;
    setCopied(false);
    createInviteMutation.mutate();
  };

  // 招待URLをコピー
  const copyInviteUrl = async () => {
    try {
      if (inviteUrl) {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      const ta = document.createElement('textarea');
      ta.value = inviteUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // 招待URLをクリア
  const clearInvite = () => {
    if (!inviteUrl) return;

    setConfirmModal({
      isOpen: true,
      title: '招待URLのクリア',
      message: 'すでに招待URLが発行されています。クリアすると、このURLは無効になります。',
      targetName: inviteUrl,
      warning: '⚠️ この操作は取り消せません。再度招待URLを作成する場合は、新しいURLが発行されます。',
      confirmText: 'クリアする',
      confirmButtonClass: 'btn-danger',
      onConfirm: () => {
        const t = new URL(inviteUrl).searchParams.get('t') || '';
        if (t) {
          deleteInviteMutation.mutate(t);
        }
      },
    });
  };

  /* ===== レンダリング ===== */
  return (
    <div className="um-root">
     
      <div className="um-filterCard">
        <div className="um-filterHeader">
          <div className="listTitle">ユーザー管理</div>
          <div className="um-filterHeaderRight">
            <span className="um-userCount">{filteredUsers.length} 件</span>

            {/* {inviteUrl && (
              <div className="um-inviteUrlBox">
                <input
                  className="um-inviteUrlInput"
                  value={inviteUrl}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  aria-label="招待URL"
                />
                <button className="btn-secondary btn-sm" onClick={copyInviteUrl}>
                  {copied ? 'コピー済' : 'コピー'}
                </button>
                <button className="btn-secondary btn-sm" onClick={clearInvite}>
                  クリア
                </button>
              </div>
            )}
            <button
              className="btn btn-primary"
              onClick={createInvite}
              disabled={createInviteMutation.isPending || !!inviteUrl}
              title={inviteUrl ? '既にURLがあります（クリアで削除）' : 'ユーザー招待リンクを作成'}
            >
              {createInviteMutation.isPending ? '作成中…' : '➕ ユーザー招待'}
            </button> */}
          </div>
        </div>

        <div className="filterBar">
          <input
            className="searchInput"
            placeholder="名前・メール・電話番号で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="umFilterSelectWrapper">
            <select
              className="umFilterSelect"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">すべての権限</option>
              <option value="active">有効のみ</option>
              <option value="inactive">無効のみ</option>
            </select>
          </div>

          <div className="umFilterSelectWrapper">
            <select
              className="umFilterSelect"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">すべての役割</option>
              <option value="管理者">管理者のみ</option>
              <option value="運用者">運用者のみ</option>
              <option value="利用者">利用者のみ</option>
            </select>
          </div>

          <button className="ghostButton" onClick={handleResetFilters}>
            条件リセット
          </button>

          <div className="um-sortGroup">
            <span className="um-sortLabel">並び替え:</span>
            <div className="umFilterSelectWrapper">
              <select
                className="umFilterSelect"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="umFilterSelectWrapper">
              <select
                className="umFilterSelect"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                {SORT_ORDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {isError && <div className="error-alert">{error.message}</div>}

      {isLoading && <div className="empty">読み込み中...</div>}

      {!isLoading && !isError && (
        <div className="um-tableWrapper">
          <table className="um-table">
            <thead>
              <tr>
                <th>名前</th>
                <th>メールアドレス</th>
                <th>電話番号</th>
                <th>役割</th>
                <th>権限</th>
                <th>最終ログイン</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="um-table-empty">
                    ユーザーがいません
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isMe = (user.user_email || '') === meEmail;
                  const isAdmin = user.org_role === '管理者';

                  return (
                    <tr
                      key={user.user_id}
                      onDoubleClick={() => handleUserClick(user)}
                      className={`um-table-row ${!user.is_active ? 'is-inactive' : ''} ${isMe ? 'is-me' : ''} ${isAdmin ? 'is-admin' : ''}`}
                    >
                      <td className="um-name-cell" data-label="名前">
                        <div className="um-name-inner">
                          <span className="um-name-text">{user.user_name}</span>
                          {isMe && <span className="um-me-badge">自分</span>}
                        </div>
                      </td>
                      <td className="um-email-cell" data-label="メールアドレス">{user.user_email || '—'}</td>
                      <td data-label="電話番号">{user.phone || '—'}</td>
                      <td data-label="役割">
                        <span className={`um-badge ${getRoleBadgeClass(user.org_role)}`}>
                          {user.org_role || '未設定'}
                        </span>
                      </td>
                      <td data-label="権限">
                        <span className={`um-badge ${user.is_active ? 'um-badge--active' : 'um-badge--inactive'}`}>
                          {user.is_active ? '有効' : '無効'}
                        </span>
                      </td>
                      <td data-label="最終ログイン">{formatDateTime(user.last_login)}</td>
                      <td className="um-actions-cell" data-label="操作" onClick={(e) => e.stopPropagation()}>
                        <button className="um-action-btn" onClick={() => handleEdit(user)}>✏️</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <UserFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingUser(null);
        }}
        mutation={updateUserMutation}
        user={editingUser}
        isMe={(editingUser?.user_email || '') === meEmail}
      />

      <UserDetailModal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onEdit={handleEdit}
        isMe={(selectedUser?.user_email || '') === meEmail}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        targetName={confirmModal.targetName}
        warning={confirmModal.warning}
        confirmText={confirmModal.confirmText}
        confirmButtonClass={confirmModal.confirmButtonClass}
      />
    </div>
  );
}